import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  RemoveCustomFieldProofMutationResponse,
  RemoveCustomFieldProofPathParams,
} from "../types/RemoveCustomFieldProof"

type RemoveCustomFieldProofClient = typeof client<
  RemoveCustomFieldProofMutationResponse,
  Error,
  never
>
type RemoveCustomFieldProof = {
  data: RemoveCustomFieldProofMutationResponse
  error: Error
  request: never
  pathParams: RemoveCustomFieldProofPathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<RemoveCustomFieldProofClient>>
  client: {
    parameters: Partial<Parameters<RemoveCustomFieldProofClient>[0]>
    return: Awaited<ReturnType<RemoveCustomFieldProofClient>>
  }
}
/**
 * @description Remove the proof of a custom field value
 * @summary Remove custom field proof
 * @link /v1/custom-field/remove-custom-field-proof/:customFieldValueId
 */
export function useRemoveCustomFieldProof(
  customFieldValueId: RemoveCustomFieldProofPathParams["customFieldValueId"],
  options: {
    mutation?: UseMutationOptions<
      RemoveCustomFieldProof["response"],
      RemoveCustomFieldProof["error"],
      RemoveCustomFieldProof["request"]
    >
    client?: RemoveCustomFieldProof["client"]["parameters"]
  } = {}
): UseMutationResult<
  RemoveCustomFieldProof["response"],
  RemoveCustomFieldProof["error"],
  RemoveCustomFieldProof["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    RemoveCustomFieldProof["response"],
    RemoveCustomFieldProof["error"],
    never
  >({
    mutationFn: async () => {
      const res = await client<
        RemoveCustomFieldProof["data"],
        RemoveCustomFieldProof["error"],
        RemoveCustomFieldProof["request"]
      >({
        method: "post",
        url: `/v1/custom-field/remove-custom-field-proof/${customFieldValueId}`,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
